import { hexToLinear, type LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';

/** The colours the painted pennants fly: orange-red, ochre yellow, and blue-grey or white. */
export type PennantColour = 'red' | 'yellow' | 'blue';

const COLOURS: readonly PennantColour[] = ['red', 'yellow', 'blue'];

/**
 * Of the 28 vessels whose pennants the museum scan shows, 11 fly red, 12 yellow and 5 blue or
 * white (docs/reference/ship-measure/pennants.json).
 */
export const PENNANT_SHARES: Readonly<Record<PennantColour, number>> = {
  red: 11 / 28,
  yellow: 12 / 28,
  blue: 5 / 28,
};

/** Each colour at the core of the painted pennants, clear of the water beside them (pennants.json). */
export const PENNANT_COLOURS: Readonly<Record<PennantColour, LinearRGB>> = {
  red: hexToLinear('#7d4515'),
  yellow: hexToLinear('#7f7123'),
  blue: hexToLinear('#64756a'),
};

/** The colour of the vessels whose pennants the painting shows apart from their neighbours'. */
export const PAINTED_PENNANTS: Readonly<Record<string, PennantColour>> = {
  'berth-R1': 'yellow',
  'berth-R2': 'yellow',
  'berth-R3': 'red',
  'berth-R4': 'blue',
  'berth-R5': 'yellow',
  'berth-R7': 'red',
  'berth-R8': 'red',
  'berth-R9': 'yellow',
  'berth-R10': 'blue',
  'berth-R16': 'red',
  'berth-R17': 'yellow',
  'berth-R18': 'red',
  'berth-R19': 'yellow',
  'berth-R20': 'red',
  'berth-R25': 'yellow',
  'berth-R26': 'red',
  'berth-R33': 'red',
  'berth-R34': 'red',
};

/** The colour a vessel's pennants fly: the painted one where the painting shows it clearly, else
    one drawn from `rng` in the painted shares. */
export function pennantColourOf(id: string, rng: Rng): PennantColour {
  return (
    PAINTED_PENNANTS[id] ??
    rng.weighted(
      COLOURS,
      COLOURS.map((colour) => PENNANT_SHARES[colour])
    )
  );
}
